import { LazyLoader } from "@CodeDaleTheme/lazy-load";
import * as CodeDaleTheme from "@CodeDaleTheme/global";
import { eventModal, checkUrlParameters } from "@CodeDaleTheme/modal";
import { initQuizSteps } from "./recommendation-quiz.js";

class QuizPopup extends HTMLElement {
  constructor() {
    super();
    this.enable = this.dataset.enable;
    this.initialized = false;
    this.sectionId = this.dataset.sectionId;
    this.scrollTriggered = false;
    this.spaceScroll = 35;
    this.contact_form = this.querySelector("form#contact_form");
  }

  connectedCallback() {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.init());
    } else {
      if ("requestIdleCallback" in window) {
        requestIdleCallback(() => this.init());
      } else {
        setTimeout(() => this.init(), 100);
      }
    }
  }

  init() {
    // In the theme editor, keep the popup in sync with section select/load/deselect.
    if (window.Shopify && window.Shopify.designMode) {
      const component = this;
      document.addEventListener("shopify:section:select", (event) => {
        component.actionDesignMode(event);
      });
      document.addEventListener("shopify:section:load", (event) => {
        component.createPopup();
      });
      document.addEventListener("shopify:section:deselect", () => {
        component.closePopup();
      });
    }
    if (this.initialized) return;
    this.initialized = true;

    const urlChecked = checkUrlParameters();
    if (urlChecked) {
      CodeDaleTheme.setCookie("quiz_popup", "true", 365);
      return;
    }
    const getCookie = CodeDaleTheme.getCookie("quiz_popup");
    if (
      (this.enable === "show-on-homepage" || this.enable === "show-all-page") &&
      getCookie === null
    ) {
      this.initScrollTrigger();
    }
    document.addEventListener(
      "modal:opened",
      this.handleModalOpened.bind(this),
    );
    this.evenSubmitForm();
  }

  initInactivityTrigger() {
    if (this._inactivityInitialized) return;
    this._inactivityInitialized = true;
    this._inactivityTimer = null;
    this._inactivityTriggered = false;
    // User-activity events that reset the inactivity countdown.
    const activityEvents = ["scroll", "click", "mousemove", "keydown"];
    const component = this;
    // Restart the idle timer; once it fires (and no modal is open) show the popup once.
    function resetTimer() {
      if (component._inactivityTimer) clearTimeout(component._inactivityTimer);
      if (component._inactivityTriggered) return;
      component._inactivityTimer = setTimeout(() => {
        if (!component._inactivityTriggered) {
          component._inactivityTriggered = true;
          if (component._inactivityTimer) {
            clearTimeout(component._inactivityTimer);
            component._inactivityTimer = null;
          }
          const activeModal =
            CodeDaleTheme.getRoot().classList.contains("open-modal");
          if (!activeModal) {
            component.createPopup();
          }
          activityEvents.forEach((event) => {
            window.removeEventListener(event, resetTimer);
          });
        }
      }, 100);
    }
    activityEvents.forEach((event) => {
      window.addEventListener(event, resetTimer, { passive: true });
    });
    resetTimer();
  }

  initScrollTrigger() {
    const component = this;
    const scrollHandler = () => {
      if (component.scrollTriggered) return;

      // Don't trigger until the visitor has scrolled past every featured banner.
      const featuredBanners = document.querySelectorAll(
        ".section-featured-product-banner",
      );
      if (featuredBanners.length > 0) {
        const currentScroll =
          window.pageYOffset || document.documentElement.scrollTop;
        let hasPassedAllBanners = true;

        featuredBanners.forEach((banner) => {
          const bannerRect = banner.getBoundingClientRect();
          const bannerBottom = bannerRect.bottom + window.pageYOffset;
          if (currentScroll < bannerBottom) {
            hasPassedAllBanners = false;
          }
        });

        if (!hasPassedAllBanners) {
          return;
        }
      }

      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const documentHeight =
        document.documentElement.scrollHeight -
        document.documentElement.clientHeight;
      const scrollPercentage = (scrollTop / documentHeight) * 100;
      // Once scrolled far enough, hand off to the inactivity trigger and stop listening.
      if (scrollPercentage >= component.spaceScroll) {
        component.scrollTriggered = true;
        component.initInactivityTrigger();
        window.removeEventListener("scroll", scrollHandler);
      }
    };

    window.addEventListener("scroll", scrollHandler, { passive: true });
  }

  actionDesignMode(event) {
    const component = this;
    const currentTarget = event.target;
    const wrapper = document.querySelector("quiz-modal-popup");
    const template = currentTarget.querySelector("quiz-modal-popup");
    // Open the popup when this section is selected in the editor; close it otherwise.
    if (
      JSON.parse(currentTarget.dataset.shopifyEditorSection).id ===
      this.sectionId
    ) {
      if (!wrapper.classList.contains("active")) {
        component.createPopup(template);
      }
    } else {
      if (wrapper.classList.contains("active")) {
        component.closePopup();
      }
    }
  }

  createPopup(templateDesignMode) {
    let template;
    if (window.Shopify && window.Shopify.designMode) {
      const existingPopup = document.querySelector("quiz-modal-popup");
      if (existingPopup.classList.contains("active")) {
        eventModal(existingPopup, "close", true);
      }
    }
    if (window.Shopify && window.Shopify.designMode) {
      template = templateDesignMode;
    } else {
      template = this.querySelector("quiz-modal-popup");
    }
    if (!template) return;
    eventModal(template, "open", true, null, true);
    CodeDaleTheme.global.rootToFocus = template;
    this.initNotShow(template);
    initQuizSteps(template);
  }

  handleModalOpened() {
    if (this.tagName === "QUIZ-POPUP") {
      new LazyLoader(".image-lazy-load");
    }
  }

  closePopup() {
    const wrapper = document.querySelector("quiz-modal-popup");
    if (wrapper.classList.contains("active")) {
      eventModal(wrapper, "close", true);
    }
  }

  initNotShow(modal) {
    // Wire up the "don't show again" checkbox inside the popup, if present.
    const notShowCheckbox = modal?.querySelector(".quiz-action");
    if (!notShowCheckbox || notShowCheckbox.type !== "checkbox") return;
    const component = this;

    notShowCheckbox.addEventListener("change", () => {
      component.eventNotShow(notShowCheckbox);
    });
  }

  eventNotShow(checkbox) {
    if (checkbox.checked) {
      CodeDaleTheme.setCookie("quiz_popup", "true", 1);
    } else {
      CodeDaleTheme.deleteCookie("quiz_popup");
    }
  }

  evenSubmitForm() {
    if (this.contact_form) {
      this.contact_form.addEventListener("submit", (e) => {
        const submitButton =
          e.submitter ||
          this.contact_form.querySelector('button[type="submit"]');
        const spinner = submitButton?.querySelector(".icon-load");
        const text = submitButton?.querySelector(
          ".hidden-on-load.transition-short",
        );

        submitButton?.classList.add("loading");
        spinner?.classList.remove("opacity-0", "pointer-none");
        text?.classList.add("opacity-0");
      });
    }
  }
}
customElements.define("quiz-popup", QuizPopup);